import { useState, useEffect } from "react";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import FunctionalButton from "components/layout/FunctionalButton";
import { ReactComponent as CloseIcon } from "assets/svgs/Close.svg";

const StairTypeDialog = ({
    visible,
    onConfirm,
    onClose,
    /** Above mission action toolbar (z-[100]) and other fixed control chrome. */
    zIndexClassName = "z-[200]",
}) => {
    const [direction, setDirection] = useState(null); // up or down, no default
    const [landing, setLanding] = useState(0);
    const [levels, setLevels] = useState(1);
    const [correctionMode, setCorrectionMode] = useState("vision");

    // Reset direction to null every time dialog opens
    useEffect(() => {
        if (visible) {
            setDirection(null);
            setCorrectionMode("vision");
        }
    }, [visible]);

    const handleConfirm = () => {
        if (!direction) {
            // User must choose direction
            return;
        }
        onConfirm({
            direction,
            landing,
            levels,
            correction_mode: correctionMode,
        });
        // Hide dialog after confirming
        onClose();
    };

    return (
        <AnimatedModal
            visible={visible}
            onClose={onClose}
            zIndexClassName={zIndexClassName}
            // Cap height with dvh so short landscape tablets keep Confirm on-screen;
            // body scrolls, footer stays pinned (overflow-hidden overrides modal default).
            className="relative flex w-96 max-h-[min(85dvh,90vh)] flex-col overflow-hidden rounded-r10 bg-black100 p-6"
        >
            <FunctionalButton
                icon={<CloseIcon />}
                className="absolute right-3 top-3 z-[1]"
                onClick={onClose}
            />
            <h2 className="mb-4 shrink-0 pr-8 text-h4l">Navigate Stairs:</h2>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain">
                {/* Direction */}
                <div>
                    <label className="mb-2 block text-h5">Direction:</label>
                    <div className="flex space-x-4">
                        <label className="flex cursor-pointer items-center space-x-2">
                            <input
                                type="radio"
                                name="direction"
                                value="up"
                                checked={direction === "up"}
                                onChange={(e) => setDirection(e.target.value)}
                                className="h-4 w-4"
                            />
                            <span className="text-h5">Up</span>
                        </label>
                        <label className="flex cursor-pointer items-center space-x-2">
                            <input
                                type="radio"
                                name="direction"
                                value="down"
                                checked={direction === "down"}
                                onChange={(e) => setDirection(e.target.value)}
                                className="h-4 w-4"
                            />
                            <span className="text-h5">Down</span>
                        </label>
                    </div>
                </div>

                {/* Correction mode */}
                <div>
                    <label className="mb-2 block text-h5">Correction:</label>
                    <div className="flex space-x-4">
                        <label className="flex cursor-pointer items-center space-x-2">
                            <input
                                type="radio"
                                name="correctionMode"
                                value="vision"
                                checked={correctionMode === "vision"}
                                onChange={(e) => setCorrectionMode(e.target.value)}
                                className="h-4 w-4"
                            />
                            <span className="text-h5">Vision</span>
                        </label>
                        <label className="flex cursor-pointer items-center space-x-2">
                            <input
                                type="radio"
                                name="correctionMode"
                                value="pose"
                                checked={correctionMode === "pose"}
                                onChange={(e) => setCorrectionMode(e.target.value)}
                                className="h-4 w-4"
                            />
                            <span className="text-h5">Pose</span>
                        </label>
                    </div>
                    <p className="mt-1 text-h5 text-black60">
                        Vision uses camera alignment; pose holds position from saved pose
                    </p>
                </div>

                {/* Landing */}
                <div>
                    <label className="mb-2 block text-h5">Landing:</label>
                    <div className="flex items-center space-x-3">
                        <FunctionalButton
                            title="-"
                            onClick={() => setLanding(Math.max(0, landing - 1))}
                            className="flex h-8 w-8 items-center justify-center"
                            disabled={landing === 0}
                        />
                        <span className="w-12 text-center text-h5">{landing}</span>
                        <FunctionalButton
                            title="+"
                            onClick={() => setLanding(landing + 1)}
                            className="flex h-8 w-8 items-center justify-center"
                        />
                    </div>
                    <p className="mt-1 text-h5 text-black60">
                        Intermediate landings (0 = single flight)
                    </p>
                </div>

                {/* Levels */}
                <div>
                    <label className="mb-2 block text-h5">Levels:</label>
                    <div className="flex items-center space-x-3">
                        <FunctionalButton
                            title="-"
                            onClick={() => setLevels(Math.max(1, levels - 1))}
                            className="flex h-8 w-8 items-center justify-center"
                            disabled={levels === 1}
                        />
                        <span className="w-12 text-center text-h5">{levels}</span>
                        <FunctionalButton
                            title="+"
                            onClick={() => setLevels(levels + 1)}
                            className="flex h-8 w-8 items-center justify-center"
                        />
                    </div>
                    <p className="mt-1 text-h5 text-black60">
                        Times to repeat the full stair sequence
                    </p>
                </div>
            </div>

            <div className="mt-4 flex shrink-0 justify-between space-x-2">
                <FunctionalButton
                    title="Close"
                    titleClassName="text-black60"
                    className="cancel-button"
                    onClick={onClose}
                />
                <FunctionalButton
                    title="Confirm"
                    className="h-7 w-20 rounded-r5 bg-primary2"
                    onClick={handleConfirm}
                    disabled={!direction}
                />
            </div>
        </AnimatedModal>
    );
};

export default StairTypeDialog;
